// common/src/redux/resource/inserAnimalEvents/eventAnimalInsertAncestrySlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiPage } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/vnos/vnosPoreklo";

// Page search (generic)
export const findUpsertByPoreklo = createAsyncThunk(
  "eventAnimalInsertAncestry/findPage",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.message || "Search failed");
    }
  }
);

// Find single vnosPoreklo by zivalId (first item)
export const findVnosPorekloByZivalId = createAsyncThunk(
  "eventAnimalInsertAncestry/findByZivalId",
  async ({ zivalId }, { rejectWithValue }) => {
    try {
      const filter = `zival.id:EQ:'${zivalId}'`;
      const res = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        { page: 0, size: 1 },
        filter,
        null,
        false
      );

      const item = res?.data?.content?.[0] ?? null;
      return { zivalId, item };
    } catch (e) {
      return rejectWithValue(e?.response?.data ?? e?.message ?? e);
    }
  }
);

export const getVnosPorekloById = createAsyncThunk(
  "eventAnimalInsertAncestry/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await ApiClient.get(`${baseUrl}/${id}`);
      return res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data ?? e?.message ?? "Load failed");
    }
  }
);

// POST create, PUT update
export const saveVnosPoreklo = createAsyncThunk(
  "eventAnimalInsertAncestry/save",
  async (body, { rejectWithValue }) => {
    try {
      if (!body) return rejectWithValue("No body");

      const isUpdate = !!body?.id;
      const res = isUpdate
        ? await ApiClient.put(`${baseUrl}/${body.id}`, body)
        : await ApiClient.post(baseUrl, body);

      return res.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data ?? e?.message ?? e);
    }
  }
);

export const deleteVnosPoreklo = createAsyncThunk(
  "eventAnimalInsertAncestry/delete",
  async ({ id }, { rejectWithValue }) => {
    try {
      if (!id) return rejectWithValue("No id");
      const res = await ApiClient.delete(`${baseUrl}/${id}`);
      return { id, data: res.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data ?? e?.message ?? e);
    }
  }
);
export const findVnosPorekloByKey = createAsyncThunk(
  "eventAnimalInsertAncestry/findVnosPorekloByKey",
  async (
    { key, page = null, filter = null, sort = null },
    { rejectWithValue }
  ) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page instanceof ApiPage ? page.toQueryString() : page,
        filter,
        sort,
        false
      );
      return { key, data: response.data };
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
const initialState = {
  isLoading: false,
  error: null,

  page: {},

  // cache: zivalId -> vnosPoreklo
  byZivalId: {},
  pageByKey: {},
  isLoadingByKey: {},
  isErrorByKey: null,
  lastSaved: null,
  lastDeletedId: null,
};

const slice = createSlice({
  name: "eventAnimalInsertAncestry",
  initialState,
  reducers: {},
  extraReducers: (b) => {
    b.addCase(findVnosPorekloByZivalId.pending, (s) => {
      s.isLoading = true;
      s.error = null;
    });
    b.addCase(findVnosPorekloByZivalId.fulfilled, (s, a) => {
      s.isLoading = false;
      const { zivalId, item } = a.payload || {};
      if (zivalId) s.byZivalId[zivalId] = item;
    });
    b.addCase(findVnosPorekloByZivalId.rejected, (s, a) => {
      s.isLoading = false;
      s.error = a.payload ?? "Error";
    });

    b.addCase(findUpsertByPoreklo.pending, (s) => {
      s.isLoading = true;
      s.error = null;
    });
    b.addCase(findUpsertByPoreklo.fulfilled, (s, a) => {
      s.isLoading = false;
      s.page = a.payload;
    });
    b.addCase(findUpsertByPoreklo.rejected, (s, a) => {
      s.isLoading = false;
      s.error = a.payload ?? "Error";
    });

    b.addCase(saveVnosPoreklo.pending, (s) => {
      s.isLoading = true;
      s.error = null;
    });
    b.addCase(saveVnosPoreklo.fulfilled, (s, a) => {
      s.isLoading = false;
      s.lastSaved = a.payload;
    });
    b.addCase(saveVnosPoreklo.rejected, (s, a) => {
      s.isLoading = false;
      s.error = a.payload ?? "Error";
    });

    b.addCase(deleteVnosPoreklo.pending, (s) => {
      s.isLoading = true;
      s.error = null;
    });
    b.addCase(deleteVnosPoreklo.fulfilled, (s, a) => {
      s.isLoading = false;
      s.lastDeletedId = a.payload?.id ?? null;
    });
    b.addCase(deleteVnosPoreklo.rejected, (s, a) => {
      s.isLoading = false;
      s.error = a.payload ?? "Error";
    });

    b.addCase(findVnosPorekloByKey.pending, (state, action) => {
      const { key } = action.meta.arg;
      state.isLoadingByKey[key] = true;
      state.errorByKey[key] = null;
    })
      .addCase(findVnosPorekloByKey.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.isLoadingByKey[key] = false;
        state.pageByKey[key] = data || {};
      })
      .addCase(findVnosPorekloByKey.rejected, (state, action) => {
        const { key } = action.meta.arg;
        state.isLoadingByKey[key] = false;
        state.errorByKey[key] = action?.payload || action?.error?.message || {};
      });
  },
});

export default slice.reducer;
